import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { HandHeart, Receipt as ReceiptIcon, MessageCircle, Check } from "lucide-react";

export interface BridgeReturnPayload {
  category: string | null;
  poBoxCode: string | null;
  poBoxLabel: string | null;
  receiptId: string | null;
  isGuest: boolean;
  message: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: BridgeReturnPayload | null;
}

function formatCategory(slug: string | null): string {
  if (!slug) return "your chosen lane";
  return slug
    .split("-")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function BridgeReturnDialog({ open, onOpenChange, payload }: Props) {
  if (!payload) return null;

  const categoryLabel = payload.poBoxLabel || formatCategory(payload.category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-bridge-return">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30">
            <Check className="h-6 w-6 text-amber-500" />
          </div>
          <DialogTitle className="text-center flex items-center justify-center gap-2">
            <HandHeart className="h-5 w-5 text-amber-500" />
            Welcome Home
          </DialogTitle>
          <DialogDescription className="text-center">
            {payload.message || "Your offering has been received."}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/40 p-4 space-y-2" data-testid="summary-bridge-return">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Given to</span>
            <span className="font-medium" data-testid="text-return-category">{categoryLabel}</span>
          </div>
          {payload.poBoxCode && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">PO Box</span>
              <span className="font-mono text-xs" data-testid="text-return-pobox">{payload.poBoxCode}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Status</span>
            <span className="text-emerald-500 font-medium">Received</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
          {!payload.isGuest && (
            <Link href="/giving/receipts" onClick={() => onOpenChange(false)}>
              <Button variant="outline" size="sm" className="w-full gap-2" data-testid="button-return-view-receipt">
                <ReceiptIcon className="h-4 w-4" />
                View receipt
              </Button>
            </Link>
          )}
          <Link href="/" onClick={() => onOpenChange(false)}>
            <Button
              size="sm"
              className={`w-full gap-2 ${payload.isGuest ? "sm:col-span-2" : ""}`}
              data-testid="button-return-continue-chat"
            >
              <MessageCircle className="h-4 w-4" />
              Return to Chat
            </Button>
          </Link>
        </div>

        {payload.isGuest && (
          <p className="text-xs text-center text-muted-foreground pt-1">
            Sign in to track your giving history across sessions.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
